/**
 * Feature Gate Utilities - Phase 2 Release Control & Maintenance Mode
 * 
 * IMPORTANT: These gates control visibility of Phase 2 features and site access.
 * 
 * ============================================================================
 * MAINTENANCE MODE (PUBLIC SITE LOCKDOWN)
 * ============================================================================
 * 
 * When MAINTENANCE_MODE = true:
 * - Public visitors see an elegant "Coming Soon" holding page
 * - /admin remains fully accessible
 * - Admin can bypass maintenance by adding ?access=carol2026 to any URL
 * 
 * TO DISABLE MAINTENANCE MODE:
 * Change MAINTENANCE_MODE to false below, then rebuild/refresh
 * 
 * BYPASS URL PARAMETER: ?access=carol2026
 * This sets localStorage so you only need to add it once per browser session
 * ============================================================================
 * 
 * Current Phase 2 Features:
 * - RSVP guest-facing routes and APIs
 * 
 * Usage:
 *   import { isPhase2Released, isRsvpEnabled, isMaintenanceMode } from '../utils/featureGates';
 *   if (isMaintenanceMode()) return <MaintenanceScreen />;
 */

// ============================================================================
// MAINTENANCE MODE CONTROL - SET TO false TO REOPEN PUBLIC SITE
// ============================================================================
const MAINTENANCE_MODE = false;
const MAINTENANCE_BYPASS_KEY = 'carol2026';
// ============================================================================

// Read environment variables (set at build time)
const PHASE_2_RELEASE = process.env.REACT_APP_PHASE_2_RELEASE === 'true';
const RSVP_ENABLED = process.env.REACT_APP_RSVP_ENABLED === 'true';

/**
 * Check if site is in maintenance mode.
 * Returns false if:
 * - MAINTENANCE_MODE is false
 * - User has bypass key in localStorage
 * - URL contains bypass parameter
 */
export const isMaintenanceMode = () => {
  if (!MAINTENANCE_MODE) return false;
  
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    const accessParam = urlParams.get('access');
    
    // Legacy URL-param bypass (still works)
    if (accessParam === MAINTENANCE_BYPASS_KEY) {
      localStorage.setItem('maintenance_bypass', 'true');
      return false;
    }
    
    // Legacy localStorage bypass
    if (localStorage.getItem('maintenance_bypass') === 'true') {
      return false;
    }

    // Secure admin bypass token — issued by backend after password validation
    if (localStorage.getItem('admin_bypass_token')) {
      return false;
    }
  }
  
  return true;
};

/**
 * Clear maintenance bypass (for testing)
 */
export const clearMaintenanceBypass = () => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('maintenance_bypass');
  }
};

/**
 * Check if Phase 2 is released.
 * All Phase 2 features are gated behind this master flag.
 */
export const isPhase2Released = () => PHASE_2_RELEASE;

/**
 * Check if RSVP is effectively enabled.
 * Requires both PHASE_2_RELEASE=true AND RSVP_ENABLED=true.
 */
export const isRsvpEnabled = () => PHASE_2_RELEASE && RSVP_ENABLED;

/**
 * Get list of active Phase 2 features.
 * Useful for admin visibility.
 */
export const getPhase2Features = () => ({
  phase2Released: PHASE_2_RELEASE,
  maintenanceMode: MAINTENANCE_MODE,
  features: {
    rsvp: isRsvpEnabled(),
  },
  activeFeatures: [
    ...(isRsvpEnabled() ? ['RSVP'] : []),
    // Add future Phase 2 features here
  ],
});

/**
 * Log Phase 2 status to console (for debugging).
 */
export const logPhase2Status = () => {
  const status = getPhase2Features();
  console.log('[PHASE 2]', status.phase2Released ? 'RELEASED' : 'NOT RELEASED');
  console.log('[MAINTENANCE]', status.maintenanceMode ? 'ACTIVE' : 'DISABLED');
  console.log('[PHASE 2] Active features:', status.activeFeatures.join(', ') || 'None');
};

export default {
  isPhase2Released,
  isRsvpEnabled,
  isMaintenanceMode,
  clearMaintenanceBypass,
  getPhase2Features,
  logPhase2Status,
};
